/**
 * AnalyticsModal Component
 * Interactive dashboard for Daily, Weekly, and Monthly progress tracking.
 */
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, BarChart2 } from 'lucide-react';

export function AnalyticsModal({ isOpen, onClose, stats, gameState }) {
  const [activeTab, setActiveTab] = useState('daily');

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="analytics-backdrop" onClick={onClose}>
        <motion.div
          className="analytics-modal"
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
        >
          {/* Header */}
          <div className="analytics-header">
            <div className="analytics-title-group">
              <BarChart2 size={20} className="analytics-icon" />
              <h2>Tiến Độ & Năng Suất</h2>
            </div>
            <button className="analytics-close-btn" onClick={onClose} aria-label="Đóng bảng thống kê">
              <X size={18} />
            </button>
          </div>

          {/* Period Tabs */}
          <div className="analytics-tabs">
            <button className={`analytics-tab-btn ${activeTab === 'daily' ? 'active' : ''}`} onClick={() => setActiveTab('daily')}>
              Hôm nay
            </button>
            <button className={`analytics-tab-btn ${activeTab === 'weekly' ? 'active' : ''}`} onClick={() => setActiveTab('weekly')}>
              Tuần này
            </button>
            <button className={`analytics-tab-btn ${activeTab === 'monthly' ? 'active' : ''}`} onClick={() => setActiveTab('monthly')}>
              Tháng này
            </button>
          </div>

          {/* Tab Content */}
          <div className="analytics-body">
            {activeTab === 'daily' && (
              <div className="analytics-section">
                <div className="analytics-summary-cards">
                  <div className="summary-card">
                    <span className="card-label">Hoàn thành hôm nay</span>
                    <strong className="card-value text-emerald-500">{stats.todayCompletedCount} task</strong>
                  </div>
                  <div className="summary-card">
                    <span className="card-label">XP gặt hái</span>
                    <strong className="card-value text-amber-500">+{stats.todayXP} XP</strong>
                  </div>
                  <div className="summary-card">
                    <span className="card-label">Chuỗi ngày (Streak)</span>
                    <strong className="card-value text-orange-500">🔥 {gameState.currentStreak} ngày</strong>
                  </div>
                </div>

                <h4 className="section-subtitle">Nhiệm vụ xong hôm nay ({stats.todayCompletedTasks.length})</h4>
                <div className="completed-log-list">
                  {stats.todayCompletedTasks.length === 0 ? (
                    <p className="empty-log-text">Chưa có task nào hoàn thành hôm nay. Hãy bắt đầu ngay!</p>
                  ) : (
                    stats.todayCompletedTasks.map((t) => (
                      <div key={t.id} className="completed-log-item">
                        <span className="log-badge">✓</span>
                        <span className="log-text">{t.text}</span>
                        <span className="log-quadrant">{t.quadrantTitle}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'weekly' && (
              <div className="analytics-section">
                <div className="weekly-header-info">
                  <span>Tổng tuần này: <strong>{stats.weeklyTotal} tasks</strong></span>
                  <span>Kỷ lục streak: <strong>{gameState.highestStreak} ngày</strong></span>
                </div>

                {/* 7-day bar chart */}
                <div className="weekly-chart-container">
                  {stats.weeklyDays.map((d) => {
                    const heightPercent = stats.weeklyTotal > 0
                      ? Math.min(100, Math.round((d.count / Math.max(1, stats.weeklyTotal)) * 100 * 2))
                      : 0;
                    return (
                      <div key={d.date} className={`weekly-bar-col ${d.isToday ? 'today' : ''}`}>
                        <span className="bar-count">{d.count}</span>
                        <div className="bar-track">
                          <div className="bar-fill" style={{ height: `${Math.max(6, heightPercent)}%` }}></div>
                        </div>
                        <span className="bar-label">{d.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'monthly' && (
              <div className="analytics-section">
                <div className="monthly-summary-info">
                  <span>Tháng này đã hoàn thành: <strong>{stats.monthCompletedCount} tasks</strong></span>
                  <span>Tổng mọi thời đại: <strong>{stats.totalCompletedAllTime} tasks</strong></span>
                </div>

                <h4 className="section-subtitle">Bản đồ nhiệt 28 ngày qua</h4>
                <div className="heatmap-grid">
                  {stats.monthlyDays.map((d) => (
                    <div
                      key={d.date}
                      className={`heatmap-cell level-${d.level} ${d.isToday ? 'today' : ''}`}
                      title={`${d.date}: ${d.count} task hoàn thành`}
                    />
                  ))}
                </div>
                <div className="heatmap-legend">
                  <span className="legend-label">Ít hơn</span>
                  <span className="heatmap-cell level-0"></span>
                  <span className="heatmap-cell level-1"></span>
                  <span className="heatmap-cell level-2"></span>
                  <span className="heatmap-cell level-3"></span>
                  <span className="legend-label">Nhiều hơn</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
